<template lang="pug">
div.neutral200
  v-container.pa-8(style="max-width:900px")
    div.d-flex.justify-center.mt-16(v-if="isFetching")
      v-progress-circular(:width="2" :size="50" color="primary" indeterminate)

    template(v-else)
      v-row(no-gutters align="center")
        v-col.shrink
          v-btn.ml-n3(text icon @click="goToServices" height)
            v-icon(color="basic800" :size="24") mdi-arrow-left
        v-col.ml-3
          .text-2xl.font-semibold.neutral700--text {{ isHqProduct ? 'View service' : (id ? $t('edit_service') : $t('create_new_service')) }}

      v-form(ref="form")
        //- ==================== HQ VIEW MODE ====================
        template(v-if="isHqProduct")
          //- Inventory Details (read-only)
          v-card.pa-6.mt-4.round-xl(outlined)
            .text-md.font-semibold.neutral700--text Service details
            v-row.mt-3
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text {{ $t('name') }}
                div.text-sm.neutral700--text.mt-2 {{ serviceData.product.name }}
                yz-badge.mt-1(size="sm" color="blue-100" text-color="blue-500" border) HQ
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Type
                div.text-sm.neutral700--text.mt-2.text-capitalize {{ serviceData.type || '-' }}
            v-row.mt-3
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Description
                div.text-sm.neutral700--text.mt-2 {{ serviceData.product.description || 'Not available' }}
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Cost
                div.text-sm.neutral700--text.mt-2 {{ $currency.symbol }} {{ (serviceData.cost / $currency.multiplier).toFixed(2) }}
            v-row.mt-3
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Group
                div.text-sm.neutral700--text.mt-2 {{ groupNames }}

          //- Pricing (HQ default + override)
          v-card.pa-6.mt-4.round-xl(outlined)
            .text-md.font-semibold.neutral700--text Pricing
            div.d-flex.align-center.mt-2
              .yz-regular-body-sm Pricing tiers for this item. You can manage your pricing tier in settings.
              y-button(label="Go to pricing tier setting" size="xs" color="transparent" text-color="neutral700" icon-color="neutral700" icon-size="15" underline button-class="px-0" append-icon="mdi-arrow-top-right" @click="goToPriceTiers")

            //- HQ default pricing (read-only)
            template(v-if="formattedHqPriceTiers.length > 0")
              .yz-medium-body-md.text-neutral-700.mt-6 Default pricing
              .yz-regular-body-sm.mt-1 This pricing has been set by HQ and shared across all branches.
              v-simple-table.mt-3
                template(v-slot:default)
                  thead
                    tr
                      th.yz-medium-body-xs.text-neutral-700 Price tier
                      th.yz-medium-body-xs.text-neutral-700 Payment method
                      th.yz-medium-body-xs.text-neutral-700 Default price
                  tbody
                    tr(v-for="hqTier in formattedHqPriceTiers" :key="'hq-' + hqTier.id")
                      td.py-3
                        .yz-heading-9.text-neutral-700 {{ hqTier.name }}
                        .yz-regular-body-sm {{ hqTier.description }}
                      td.py-3
                        .d-flex.align-center.gap-2
                          div
                            .yz-regular-body-xs.text-neutral-700 Panel: {{ hqTier.panelText }}
                            .yz-regular-body-xs.text-neutral-700.mt-1 Self-Pay: {{ hqTier.selfPayText }}
                          yz-icon.text-neutral-500.cursor-pointer(v-if="hqTier.panelText !== 'None included'" size="16" @click="openDetails(hqTier.tierObj)") mdi-information-outline
                      td.py-3
                        .yz-regular-body-sm.text-neutral-700 {{ $currency.symbol }} {{ hqTier.price }}

            //- Branch override pricing
            div.d-flex.align-start.justify-space-between.mt-6
              div
                .yz-medium-body-md.text-neutral-700 Add default pricing override
                .yz-regular-body-sm.mt-1 This pricing will override the default pricing and adjusted according to your branch's pricing setup.
              y-button(v-if="canEditPriceOverride" label="Edit" size="sm" @click="onEditOverride")

            template(v-if="overridePriceTiers.length > 0")
              v-simple-table.mt-4
                template(v-slot:default)
                  thead
                    tr
                      th.yz-medium-body-xs.text-neutral-700 Branches
                      th.yz-medium-body-xs.text-neutral-700 Price tier
                      th.yz-medium-body-xs.text-neutral-700 Payment method
                      th.yz-medium-body-xs.text-neutral-700 Override price
                  tbody
                    //- Branch row (expandable)
                    tr.cursor-pointer(:class="{ 'override-branch-expanded': overrideBranchExpanded }" @click="overrideBranchExpanded = !overrideBranchExpanded")
                      td.py-3
                        .d-flex.align-center.gap-2
                          .yz-heading-9.text-neutral-700 {{ company.name }}
                          yz-icon(size="16") {{ overrideBranchExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                      td
                      td
                      td
                    //- Tier sub-rows when branch expanded
                    template(v-if="overrideBranchExpanded")
                      tr(v-for="(oTier, oIdx) in formattedOverrideTiers" :key="'override-' + oIdx")
                        td
                        td.py-3
                          .yz-heading-9.text-neutral-700 {{ oTier.name }}
                          .yz-regular-body-sm {{ oTier.description }}
                        td.py-3
                          .d-flex.align-center.gap-2
                            div
                              .yz-regular-body-xs.text-neutral-700 Panel: {{ oTier.panelText }}
                              .yz-regular-body-xs.text-neutral-700.mt-1 Self-Pay: {{ oTier.selfPayText }}
                            yz-icon.text-neutral-500.cursor-pointer(v-if="oTier.panelText !== 'None included'" size="16" @click="openDetails(oTier.tierObj)") mdi-information-outline
                        td.py-3
                          .yz-regular-body-sm.text-neutral-700 {{ formatOverridePrice(oTier.price) }}
            template(v-else)
              .yz-regular-body-sm.text-neutral-500.mt-2.font-italic No override pricing set

          //- Online Availability (editable)
          v-card.pa-6.mt-4.round-xl(outlined v-if="Auth('page', '/admin/clinic/appointment')")
            div.d-flex
              div
                .text-md.font-semibold.neutral700--text Make this service available online
                .text-sm.neutral700--text List this service in your appointment page
              v-spacer
              div
                y-switch(v-model="serviceForm.booking_enabled")
            template(v-if="serviceForm.booking_enabled")
              v-divider.mt-5
              service-details(extend)
              v-divider.basic500.my-5
              service-request
              v-divider.basic500.my-5
              service-settings
              v-divider.basic500.my-5
              service-pre-questions
              v-divider.basic500.my-5
              service-pre-instructions
              v-divider.basic500.my-5
              service-post-instructions
              v-divider.basic500.my-5
              service-provider

        //- ==================== EDIT/CREATE MODE ====================
        template(v-else)
          v-card.pa-6.mt-4.round-xl(outlined)
            div.d-flex.align-center
              .text-md.font-semibold.neutral700--text Service details
              v-spacer
              yz-btn(label="View service history" size="sm" @click="$router.push({ name: 'ServiceHistory', params: { id, origin: 'ClinicEditService' } })")
            v-row.mt-3
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text {{ $t('name') }}
                v-text-field.text-sm.mt-2.round-xl(v-model="serviceForm.product.name" :placeholder="$t('name')" outlined dense height="48" :rules="rules.not_empty" hide-details="auto")
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Type
                y-select.mt-2(v-model="serviceForm.type" placeholder="Type" attach :items="[{ text: 'Service', value: 'service' }, { text: 'Procedure', value: 'procedure' }, { text: 'Investigation', value: 'investigation' }, {  text: 'Consultation', value: 'consultation' }]" item-text="text" item-value="value" :rules="rules.not_empty_select")
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Description (Optional)
                v-text-field.text-sm.mt-2.round-xl(v-model="serviceForm.product.description" placeholder="Description" outlined dense height="48" hide-details="auto")
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Cost
                v-text-field.text-sm.mt-2.round-xl(v-model="serviceForm.cost" placeholder="100" outlined dense height="48" :prefix="$currency.symbol" :rules="rules.not_empty" type="number" hide-details="auto")
              v-col(cols="6")
                div.text-sm.font-semibold.neutral500--text Group
                medication-group-dropdown.mt-2(v-model="groups" attach :error="false")

          //- Pricing
          v-card.pa-6.mt-4.round-xl(outlined)
            .text-md.font-semibold.neutral700--text Pricing
            div.d-flex.align-center.mt-2
              .text-sm.neutral700--text.mr-2 Set default pricing tier for this item. You can manage your pricing tier in settings.
              y-button(label="Go to pricing tier setting" size="xs" color="transparent" text-color="neutral700" icon-color="neutral700" icon-size="15" underline button-class="px-0" append-icon="mdi-arrow-top-right" @click="goToPriceTiers")
            v-data-table.mt-2(:headers="headers" :items="pricingTiers" item-key="id" :items-per-page="pageSize" :hide-default-header="pricingTiers.length === 0" hide-default-footer :loading="isFetchingPricingTiers" disable-sort :header-props="{ sortIcon: '$yzi-triangle' }")
              template(#no-data)
                div.empty-box.mt-6.pa-8(align="center")
                  v-avatar(color="neutral300" size="54")
                    v-icon(color="neutral500" size="30") mdi-tag-outline
                  div.text-center.text-md.font-semibold.neutral700--text.mt-6 You don't have any pricing tier yet
                  div.text-center.text-sm.font-normal.neutral500--text.mt-1 Set your pricing tier now
                  y-button.mt-6(label="Set your pricing tier" size="sm" color="neutral700" text-color="neutral100" icon-color="neutral100" icon-size="20" append-icon="mdi-arrow-top-right" @click="goToPriceTiers")
              //- name
              template(v-slot:item.name="{ item }")
                div.py-2
                  .text-sm.font-semibold.neutral700--text {{ item.name }}
                  .text-sm.neutral700--text {{ item.description }}
              //- payment
              template(v-slot:item.payment="{ item }")
                div.d-flex.align-center.py-2
                  div
                    .text-xs.neutral700--text Panel: {{ getPanelCount(item.panels) }}
                    .text-xs.neutral700--text.mt-1 Self-Pay: {{ getSelfPayCount(item.self_pay) }}
                  v-btn.ml-3(icon small @click="openDetails(item)")
                    v-icon(size="20") mdi-information-outline
              template(v-slot:item.price="{ item }")
                div(style="width: 150px;")
                  v-text-field.text-sm.my-2.round-xl(v-model="item.price" placeholder="0.00" outlined dense height="48" :prefix="$currency.symbol" :rules="item.required ? rules.not_empty : []" type="number" :background-color="allowedPriceEdit ? '' : 'neutral300'" :disabled="!allowedPriceEdit" hide-details)

          v-card.pa-6.mt-4.round-xl(outlined v-if="Auth('page', '/admin/clinic/appointment')")
            div.d-flex
              div
                .text-md.font-semibold.neutral700--text Make this service available online
                .text-sm.neutral700--text List this service in your appointment page
              v-spacer
              div
                y-switch(v-model="serviceForm.booking_enabled")
            template(v-if="serviceForm.booking_enabled")
              v-divider.mt-5
              service-details(extend)
              v-divider.basic500.my-5
              service-request
              v-divider.basic500.my-5
              service-settings
              v-divider.basic500.my-5
              service-pre-questions
              v-divider.basic500.my-5
              service-pre-instructions
              v-divider.basic500.my-5
              service-post-instructions
              v-divider.basic500.my-5
              service-provider

    v-footer.white(app inset padless elevation="3")
      v-container.px-8.py-2
        div.d-flex.align-center
          v-spacer
          y-button(:label="id ? $t('save') : $t('finish')" color="neutral700" dark :loading="isLoading" @click="saveData")

  .dialogs
    pricing-tier-details-dialog(v-if="openPricingTierDetailsDialog" :open.sync="openPricingTierDetailsDialog" :tier="selectedTier" @closed="selectedTier = null")
    override-pricing-dialog(v-if="showOverridePricingDialog" :open.sync="showOverridePricingDialog" :branches="[currentBranchForOverride]" :price-tiers="mappedHqPriceTiers" :uom-conversions="[]" :uom-conversion-enabled="false" :default-uom="null" :currency="$currency" :existing-override="currentOverrideForDialog" hide-branch-selection @save="onSaveOverride" @closed="onCloseOverrideDialog")

</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import ServiceDetails from '@/components/clinic/manage/ServiceDetails.vue'
import ServiceSettings from '@/components/clinic/manage/ServiceSettings.vue'
import ServiceRequest from '@/components/clinic/manage/ServiceRequest.vue'
import ServicePreQuestions from '@/components/clinic/manage/ServicePreQuestions.vue'
import ServicePreInstructions from '@/components/clinic/manage/ServicePreInstructions.vue'
import ServicePostInstructions from '@/components/clinic/manage/ServicePostInstructions.vue'
import ServiceProvider from '@/components/clinic/manage/ServiceProvider.vue'
import MedicationGroupDropdown from '@/components/clinic/inventory/MedicationGroupDropdown.vue'
import OverridePricingDialog from '@/components/hq/inventory/branches-warehouses/dialog/OverridePricingDialog.vue'
import PricingTierDetailsDialog from "@/components/clinic/inventory/PricingTierDetailsDialog.vue";

export default {
  name: 'Service',
  components: { ServiceDetails, ServiceSettings, ServiceRequest, ServicePreQuestions, ServicePreInstructions, ServicePostInstructions, ServiceProvider, MedicationGroupDropdown, OverridePricingDialog, PricingTierDetailsDialog },
  props: ['id', 'savePage', 'saveTab', 'saveSearch', 'saveFilters'],
  data () {
    return {
      isLoading: false,
      isFetching: false,
      isFetchingPricingTiers: false,
      openPricingTierDetailsDialog: false,
      showOverridePricingDialog: false,
      overrideBranchExpanded: false,
      pageSize: -1,
      coverPhoto: null,
      groups: [],
      photos: [],
      pricingTiers: [],
      serviceData: {},
      hqPriceTiers: [],
      overridePriceTiers: [],
      headers: [
        { text: 'Name', value: 'name', enable: true, class: 'text-table-header' },
        { text: 'Payment methods', value: 'payment', enable: true, class: 'text-table-header' },
        { text: 'Price', value: 'price', enable: true, class: 'text-table-header' },
      ],
      rules: {
        not_empty: [v => (v != null && v.toString().trim().length) || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 0 || `Amount should be more than ${this.$currency.symbol} 0`],
        not_empty_select: [v => !!v || 'This field is required']
      },
    }
  },
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany',
      form: 'clinic/getForm',
      tempService: 'clinic/getTempService',
      isDoctor: 'user/isDoctor',
    }),
    ...mapGetters('hq', ['canOverridePrice']),
    serviceForm: {
      get () {
        let data = this.tempService
        if (!data.product) {
          data.product = {}
        }
        return data
      },
      set (val) {
        this.$store.commit('clinic/updateTempService', val)
      }
    },
    isAdmin () {
      return this.member.roles.includes('Admin')
    },
    isAdminOrDoctor () {
      if (this.isAdmin || this.isDoctor) return true
      return false
    },
    isHqProduct () {
      return !!this.serviceData.product?.branch_for
    },
    isBranchHqProduct () {
      return this.isHqProduct && !!this.company?.branch_of
    },
    canEditPriceOverride () {
      if (!this.isBranchHqProduct) return true
      return this.canOverridePrice
    },
    groupNames () {
      if (!this.serviceData.groups || !this.serviceData.groups.length) return '-'
      return this.serviceData.groups.map(g => g.value || g).join(', ')
    },
    serviceProviders () {
      if (!this.serviceData.providers || !this.serviceData.providers.length) return []
      const companyName = this.serviceData.company_name || this.company.name || '-'
      return this.serviceData.providers.map(provider => ({
        branchName: companyName,
        providerName: provider.name || `${provider.first_name || ''} ${provider.last_name || ''}`.trim() || '-',
        providerType: provider.type || provider.role || '-'
      }))
    },
    formattedHqPriceTiers () {
      return this.hqPriceTiers.map(hqTier => {
        const tier = hqTier.price_tier
        return {
          id: tier.id,
          name: tier.name,
          description: tier.description,
          panelText: this.getHqTierPanelCount(tier),
          selfPayText: this.getHqTierSelfPayCount(tier),
          price: hqTier.price ? (hqTier.price / this.$currency.multiplier).toFixed(2) : '0.00',
          tierObj: tier
        }
      })
    },
    formattedOverrideTiers () {
      return this.overridePriceTiers.map(oTier => {
        const tier = oTier.tierData || {}
        return {
          ...oTier,
          name: tier.name || '',
          description: tier.description || '',
          panelText: this.getHqTierPanelCount(tier),
          selfPayText: this.getHqTierSelfPayCount(tier),
          tierObj: tier
        }
      })
    },
    mappedHqPriceTiers () {
      return this.hqPriceTiers.map(hqTier => {
        const tier = hqTier.price_tier
        return {
          id: tier.id,
          name: tier.name,
          description: tier.description,
          panelText: this.getHqTierPanelCount(tier),
          selfPayText: this.getHqTierSelfPayCount(tier),
          price: hqTier.price ? (hqTier.price / this.$currency.multiplier).toFixed(2) : null,
          price_per_uom: []
        }
      })
    },
    currentOverrideForDialog () {
      if (this.overridePriceTiers.length === 0) return null
      return {
        branches: [this.company.id],
        priceTiers: this.overridePriceTiers.map(t => ({
          price_tier: t.price_tier,
          price: t.price,
          price_per_uom: []
        }))
      }
    },
    currentBranchForOverride () {
      return { companyId: this.company.id, name: this.company.name, enabled: true }
    },
    getDisplayServiceFeeLabel () {
      if (!this.serviceData.price_type || this.serviceData.price_type === 'hide') return 'Hidden'
      if (this.serviceData.price_type === 'tier') return 'Show from pricing tier'
      if (this.serviceData.price_type === 'range') return 'Custom range'
      return 'Custom fixed'
    },
    allowedPriceEdit () {
      // create service can edit price
      if (!this.id) return true
      // admin can edit price
      if (this.isAdmin) return true
      // 'lock_treatment_plan' is for CA
      if (this.company.genes.find(gene => gene.feature === 'lock_treatment_plan') && !this.isAdminOrDoctor) return false
      // 'lock_treatment_plan_price_edit' is for doctor
      if (this.company.genes.find(gene => gene.feature === 'lock_treatment_plan_price_edit') && this.isDoctor) return false
      return true
    },
  },
  mounted () {
    if (this.id) {
      this.fetchService()
    } else {
      this.fetchPricingTiers()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchService () {
      this.isFetching = true
      this.$axios.get(`/clinics/${this.company.slug}/services/${this.id}`)
        .then(resp => {
          this.serviceData = resp.data
          this.hqPriceTiers = resp.data.hq_price_tiers || []

          if (this.isHqProduct) {
            // Populate Vuex store so sub-components can read from clinic/getService
            this.$store.commit('clinic/setService', resp.data)
            this.serviceForm = { booking_enabled: resp.data.booking_enabled }

            // Extract branch override pricing
            const branchPriceTiers = resp.data.price_tiers || []
            if (branchPriceTiers.length > 0) {
              const multiplier = Number(this.$currency.multiplier)
              this.overridePriceTiers = branchPriceTiers.map(pt => ({
                price_tier: pt.price_tier?.id || pt.price_tier,
                price: pt.price ? (pt.price / multiplier).toFixed(2) : null,
                expanded: false,
                tierData: pt.price_tier || {}
              }))
            }
            // Fetch HQ inventory settings for override permission
            if (this.company?.branch_of) {
              this.$store.dispatch('hq/fetchInventorySettings', { axios: this.$axios, hqId: this.company.branch_of.id })
            }
          } else {
            this.serviceForm = {
              product: {
                name: resp.data.product.name,
                description: resp.data.product.description
              },
              type: resp.data.type,
              cost: resp.data.cost/this.$currency.multiplier,
              booking_enabled: resp.data.booking_enabled
            }
            this.groups = resp.data.groups
            this.$store.commit('clinic/setService', resp.data)
            this.fetchPricingTiers(resp.data.price_tiers)
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    fetchPricingTiers (pricetiers) {
      this.isFetchingPricingTiers = true
      const params = {
        company: this.company.id,
        status: 'active',
        page_size: this.pageSize
      }
      this.$axios.get('product_pricetiers', { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            if (pricetiers) {
              let data = []
              resp.data.results.forEach(o => {
                let ptier = pricetiers.find(f => f.price_tier.id === o.id)
                o.price = null
                if (ptier) {
                  o.price = ptier.price / this.$currency.multiplier
                  o.required = true
                }
                data.push(o)
              })
              this.pricingTiers = data
            } else {
              this.pricingTiers = resp.data.results.map(m => { return { ...m, price: null, required: false } })
            }
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingPricingTiers = false
        })
    },
    goToPriceTiers () {
      this.$router.push('/admin/clinic/settings/price-tier-settings')
    },
    getHqTierPanelCount (tier) {
      const count = tier.panels ? tier.panels.length : 0
      if (count === 0) return 'None included'
      return count + ' included'
    },
    getHqTierSelfPayCount (tier) {
      const count = tier.self_pay ? tier.self_pay.length : 0
      if (count === 0) return 'None included'
      return count + ' included'
    },
    formatOverridePrice (price) {
      if (price === null || price === '' || price === undefined) return '—'
      return `${this.$currency.symbol} ${Number(price).toFixed(2)}`
    },
    onEditOverride () {
      this.showOverridePricingDialog = true
    },
    onSaveOverride (overrideData) {
      const multiplier = Number(this.$currency.multiplier)
      this.overridePriceTiers = overrideData.priceTiers.map(t => {
        const hqTier = this.hqPriceTiers.find(h => h.price_tier.id === t.price_tier)
        return {
          price_tier: t.price_tier,
          price: t.price,
          expanded: false,
          tierData: hqTier ? hqTier.price_tier : {}
        }
      })
      // Immediately PATCH since view mode has no Save button
      const patchData = {
        price_tiers: overrideData.priceTiers
          .filter(t => t.price !== null && t.price !== '')
          .map(t => ({
            price_tier: t.price_tier,
            price: Math.round(Number(t.price) * multiplier)
          }))
      }
      this.$axios.patch(`/clinics/${this.company.slug}/services/${this.id}`, patchData)
        .then(() => {
          this.showMessage(['success', 'Override pricing saved successfully'])
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
      this.onCloseOverrideDialog()
    },
    onCloseOverrideDialog () {
      this.showOverridePricingDialog = false
    },
    saveData () {
      if (!this.$refs.form.validate()) {
        this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
        return
      }
      this.isLoading = true
      let data = JSON.parse(JSON.stringify(this.tempService))
      if (this.isHqProduct) {
        delete data.product
        delete data.type
        delete data.cost
        delete data.groups
        delete data.price_tiers
      } else {
        data.cost = Math.round(data.cost * this.$currency.multiplier)
        data.price_tiers = this.pricingTiers.filter(f => f.price !== null && f.price !== '').map(m => { return { price_tier: m.id, price: Math.round(m.price * this.$currency.multiplier) } })
        data.groups =  this.groups.map(m => m.id)
      }
      if (this.tempService.pre_appointment || this.tempService.post_appointment) {
        data.instructions = {
          pre_appointment: this.tempService.pre_appointment,
          post_appointment: this.tempService.post_appointment
        }
        delete data.pre_appointment
        delete data.post_appointment
      }
      if (!data.booking_enabled) {
        data.booking_enabled = false
      }
      if (this.id) {
        const endpoint = `/clinics/${this.company.slug}/services/${this.id}`
        this.$axios.patch(endpoint, data)
          .then(resp => {
            let activity = 'Update service'
            this.updateActivityLog(endpoint, data, resp, 'PATCH', activity, this.id )
            this.goToServices()
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
          .finally(() => {
            this.isLoading = false
          })
      } else {
        if (data.photos) {
          this.coverPhoto = null
          this.photos = []
          data.photos.forEach((photo, index) => {
            if (index === 0) {
              this.coverPhoto = photo
            } else {
              this.photos.push(photo)
            }
          })
          delete data.photos
        }
        const endpoint = `/clinics/${this.company.slug}/services`
        this.$axios.post(endpoint, data)
          .then(resp => {
            let activity = 'Create new service'
            this.updateActivityLog(endpoint, data, resp, 'POST', activity)
            if (this.coverPhoto) {
              this.addCoverPhoto(resp.data.id)
            } else {
              this.isLoading = false
              this.goToServices()
            }
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
            this.isLoading = false
          })
      }
    },
    addCoverPhoto (serviceId) {
      const params = new FormData()
      params.append('product.image', this.coverPhoto.file)
      this.$axios.patch(`/clinics/${this.company.slug}/services/${serviceId}`, params)
        .then(() => {
          if (this.photos.length > 0) {
            this.addPhotos(serviceId)
          } else {
            this.isLoading = false
            this.goToServices()
          }
        })
        .catch(() => {
          this.isLoading = false
          this.showMessage(['error', this.$t('error_message.unable_to_upload_image')])
        })
    },
    async addPhotos (serviceId) {
      let hasError = false
      await this.asyncForEach(this.photos, async photo => {
        try {
          const params = new FormData()
          params.append('image', photo.file)
          await this.$axios.post(`/clinics/${this.company.slug}/services/${serviceId}/images`, params)
        } catch (error) {
          hasError = true
        }
      })
      this.isLoading = false
      if (hasError) {
        this.showMessage(['error', this.$t('error_message.unable_to_upload_image')])
        return
      }
      this.goToServices()
    },
    openDetails (item) {
      this.selectedTier = item
      this.openPricingTierDetailsDialog = true
    },
    getPanelCount (panels) {
      const includedPanel = panels.length
      if (this.pricingTiers.total_panels === includedPanel) {
        return 'All included'
      } else if (includedPanel === 0) {
        return 'None included'
      } else {
        return includedPanel + ' included'
      }
    },
    getSelfPayCount (methods) {
      const includedMethods = methods.length
      if (includedMethods === this.pricingTiers.total_self_pay) {
        return 'All included'
      } else if (includedMethods === 0) {
        return 'None included'
      } else {
        return includedMethods + ' included'
      }
    },
    goToServices () {
      let params = {
        savePage: this.savePage,
        saveTab: this.saveTab,
        saveSearch: this.saveSearch,
        saveFilters: this.saveFilters
      }
      this.$router.push({ name: 'ClinicServices', params })
    },
    updateActivityLog (endpoint, payload, response, method, activity, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'service',
        activity: activity,
        raw_data: { endpoint, payload, response: response.data, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    }
  },
  destroyed () {
    this.$store.commit('clinic/resetService')
    this.$store.commit('clinic/resetTempService')
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  }
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
}
.empty-box {
  position: relative;
  border-radius: 10px;
  border: var(--v-basic500) dashed 2px;
}
.button-box {
  border-left: solid 1px var(--v-neutral400);
  border-top: solid 1px var(--v-neutral400);
  border-bottom: solid 1px var(--v-neutral400);
  border-radius: 10px 0 0 10px;
  background-color: var(--v-neutral200);
}
.override-branch-expanded td {
  background-color: var(--v-neutral200);
}
</style>
